<script>
// Plinko 3D — single-player luck against the house pot.
//
// Flow:
//   1. Player picks (rows, risk) and a bet size, clicks DROP.
//   2. Contract's play(rows, risk) records a pending round + holder fee.
//   3. Oracle worker observes the new round, calls
//      resolve(roundId, xBits, zBits, seed) — `rows` coin-flips on each
//      of two axes. Contract derives finalX/finalZ, the Chebyshev `ring`
//      distance from centre, looks up the radial multiplier, settles.
//   4. UI polls every 5s; when our round flips roundStatus 0 → 1/2/3 we
//      animate the 3D drop and show the payout.
//
// 3D model: the ball tumbles down a peg pyramid. Each layer it deflects
// ±1 on X AND ±1 on Z, so after `rows` layers it sits on a
// (rows+1)×(rows+1) grid — centre bin overwhelmingly likely, corners
// exponentially rare. Payout is RADIAL: it depends only on the ring
// distance from centre. On-chain is the source of truth; the animation
// always lands on whatever (finalX,finalZ) the contract recorded.
//
// Rendering: SVG with software 3D projection — same approach as
// tttGameGrid.vue (Three.js was dropped for WebGL-sandbox flakiness).

import {
  PLINKO_CONTRACT_ADDRESS,
  PLINKO_GAME_INFO,
  PLINKO_MULTIPLIERS,
  BLOCKCHAIN_ENABLED,
  ORACLE_CONTRACT,
} from '../constants'
import { getContractStorage, tzktGet } from '../services/tzkt'

// v3 randomness helpers — player nonce + commit selection. See
// docs/V3_COMMIT_REVEAL.md.
function generateUserNonceHex() {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return '0x' + Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('')
}
function isUnrevealedPreimage(raw) {
  return raw == null || raw === '' || raw === '0x'
}
async function pickEligibleCommitId() {
  const [storage, head] = await Promise.all([
    getContractStorage(ORACLE_CONTRACT),
    tzktGet('/v1/head'),
  ])
  if (!storage) throw new Error('cannot reach oracle contract')
  if (!head) throw new Error('cannot reach tzkt head')
  const commitLog = storage.commitLog || {}
  const minCommitAge = Number(storage.minCommitAge ?? 1)
  const level = Number(head.level ?? 0)
  let bestCid = null
  let bestPostedAt = -1
  for (const [cidStr, entry] of Object.entries(commitLog)) {
    if (!isUnrevealedPreimage(entry?.revealedPreimage)) continue
    const postedAt = Number(entry?.postedAtBlock ?? 0)
    if (level - postedAt < minCommitAge) continue
    if (postedAt > bestPostedAt) {
      bestPostedAt = postedAt
      bestCid = Number(cidStr)
    }
  }
  if (bestCid === null) {
    throw new Error('no eligible commits — the oracle worker may be behind. Try again shortly.')
  }
  return bestCid
}

const ROW_OPTIONS = [8, 12, 16]
const RISK_LABELS = { 0: 'Low', 1: 'Medium', 2: 'High' }

// World-space layout constants (lattice units → SVG units). The viewBox
// is auto-fitted to the projected geometry, so these only set the
// relative proportions of the board.
const SP = 10    // horizontal spacing between adjacent lattice columns
const LH = 12    // vertical drop per pyramid layer
const BIN_DROP = 9 // extra fall from the last layer down into the bin tray
// Each bin is rendered as a well: an inset hole on the same plane as
// its rim. RIM_INSET sets how thick the ring of the rim looks (fraction
// of cell width on each side).
const RIM_INSET = 0.22

// Camera angles (radians). Pitch is FIXED — it tilts the pyramid
// forward so you see down into it, and never changes. Drag rotates
// `yaw` only: the board spins about its vertical axis like a Christmas
// tree on a turntable, never tumbling or rolling.
const FIXED_PITCH = 0.52
const DEFAULT_YAW = -0.42
// Constant turntable spin: 360° per minute (6°/s). The board has no
// user-rotate / drag controls — this is the single source of camera
// motion.
const SPIN_RAD_PER_S = (2 * Math.PI) / 60

function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3) }

// Decode a per-layer bit map from contract storage into a flat 0/1
// array. tzkt returns map values as strings — coerce to numbers.
function bitsFromStoragePath(rawPath, rows) {
  const out = []
  const src = rawPath || {}
  for (let i = 0; i < rows; i++) {
    const v = src[i] != null ? src[i] : src[String(i)]
    out.push(Number(v || 0))
  }
  return out
}

export default {
  name: 'plinkoGame',
  // previewMode: TXL-holder preview — drop()/play disabled but UI browsable.
  props: ['socket', 'wallet', 'tezos', 'previewMode'],
  data() {
    return {
      info: PLINKO_GAME_INFO,
      showInfo: false,
      rowOptions: ROW_OPTIONS,
      RISK_LABELS,
      rows: 8,
      risk: 0,
      bet: 0.1,
      fee: 0.1,
      oracleFee: 0.1,    // v3 — forwarded by the contract to the oracle
      minBet: 0.1,
      maxBet: 10.0,
      // v3 — surfaced in the status panel so the player can confirm they
      // contributed entropy. Truncated for display.
      lastUserNonceHex: '',
      lastCommitId: null,
      potBalance: 0,
      reserveBalance: 0,
      myAddress: '',
      rounds: {},
      myRoundIds: [],
      activeRoundId: null,
      lastSeenStatus: 0,
      // ball: { rows, xBits, zBits, finalX, finalZ, t0 } | null
      ball: null,
      ballSettleAt: 0,
      ballLanded: false,
      animationFrame: 0,
      ballTick: 0,
      blockchainStatus: 'Idle.',
      dropping: false,
      pollInterval: null,
      pollLastAt: 0,
      pollCountdownInterval: null,
      pollSecondsUntilNext: 5,
      // ── Camera — drag spins the board on its vertical axis ───────
      // pitch is fixed; only yaw changes (turntable rotation).
      pitch: FIXED_PITCH,
      yaw: DEFAULT_YAW,
      // Continuous-spin bookkeeping. spinAnchorYaw + spinT0 define the
      // line yaw(t) = spinAnchorYaw + (now − spinT0) × SPIN_RAD_PER_S.
      // Drag pauses the loop (spinFrame == 0) and re-anchors on release
      // so the spin resumes from the dragged-to angle.
      spinAnchorYaw: DEFAULT_YAW,
      spinT0: 0,
      spinFrame: 0,
    }
  },
  computed: {
    riskLabel() { return RISK_LABELS[this.risk] || '?' },
    // Ring-indexed multiplier array: index 0 = centre bin … rows/2 = corner.
    multipliers() {
      return (PLINKO_MULTIPLIERS[this.rows] || {})[this.risk] || []
    },
    half() { return Math.floor(this.rows / 2) },
    canDrop() {
      return (
        !this.dropping &&
        BLOCKCHAIN_ENABLED &&
        !!this.wallet &&
        this.bet >= this.minBet &&
        this.bet <= this.maxBet
      )
    },
    cantDropReason() {
      if (this.dropping) return 'Drop already in flight…'
      if (!BLOCKCHAIN_ENABLED) return 'Blockchain calls disabled in constants.js.'
      if (!this.wallet) return 'Connect your wallet to drop.'
      if (this.bet < this.minBet) return `Bet too small — min ${this.minBet} tez.`
      if (this.bet > this.maxBet) return `Bet too big — max ${this.maxBet} tez.`
      return ''
    },
    betSliderMax() {
      const dyn = Number((this.potBalance * 0.3).toFixed(3))
      return Math.max(this.minBet, Math.min(this.maxBet, dyn || this.maxBet))
    },
    activeRound() {
      if (this.activeRoundId == null) return null
      return this.rounds[this.activeRoundId] || null
    },
    pollHint() {
      if (!this.pollInterval) return 'Auto-polling off.'
      return `Next chain check in ${this.pollSecondsUntilNext}s`
    },
    pegRadius() { return Math.max(1.1, 2.4 - this.rows * 0.05) },
    // 20% smaller than the previous sizing — still scales down on the
    // denser 12/16-row boards. The sparse 8-row board gets a further 20%
    // trim so the ball doesn't read as oversized against the wide gaps.
    ballRadius() {
      const base = Math.max(2.08, (5.6 - this.rows * 0.13) * 0.8)
      return this.rows === 8 ? base * 0.8 : base
    },
    // ── Yaw-update cadence ────────────────────────────────────────
    // Bigger boards re-project more elements per yaw change, so we
    // throttle harder as `rows` grows. At 6°/s the step sizes work
    // out to: 8 rows → 0.3°/step, 12 → 0.6°/step, 16 → 0.9°/step —
    // all still below the perceptible-discreteness threshold for
    // slow rotation.
    yawUpdateMs() {
      if (this.rows >= 16) return 150
      if (this.rows >= 12) return 100
      return 50
    },
    // ── Yaw-INDEPENDENT world coords ──────────────────────────────
    // Heavy lifting that does NOT change with yaw — Vue caches these
    // so the per-yaw-tick work (pegs3d / bins3d) is just `project(...)`
    // calls + a sort, no lattice math. Critical perf win on 12/16-row
    // boards where ~500 pegs + ~300 bins re-project every yaw tick.
    pegsWorld() {
      const out = []
      for (let d = 1; d <= this.rows; d++) {
        for (let c = 0; c <= d; c++) {
          for (let e = 0; e <= d; e++) {
            // skin only: on the boundary of this layer's c×e grid
            if (c !== 0 && c !== d && e !== 0 && e !== d) continue
            const w = this.worldOf(c, e, d)
            out.push({ wx: w[0], wy: w[1], wz: w[2] })
          }
        }
      }
      return out
    },
    // For each (rows+1)² bin: precompute its rim corners, hole corners,
    // and centre point in world space. yaw changes don't invalidate.
    binsWorld() {
      const out = []
      const innerHalf = 0.5 - RIM_INSET
      for (let X = 0; X <= this.rows; X++) {
        for (let Z = 0; Z <= this.rows; Z++) {
          const ring = Math.max(Math.abs(X - this.half), Math.abs(Z - this.half))
          const rim = [
            [X - 0.5, Z - 0.5], [X + 0.5, Z - 0.5],
            [X + 0.5, Z + 0.5], [X - 0.5, Z + 0.5],
          ].map(([cx, cz]) => this.worldOf(cx, cz, this.rows, BIN_DROP))
          const hole = [
            [X - innerHalf, Z - innerHalf], [X + innerHalf, Z - innerHalf],
            [X + innerHalf, Z + innerHalf], [X - innerHalf, Z + innerHalf],
          ].map(([cx, cz]) => this.worldOf(cx, cz, this.rows, BIN_DROP))
          const center = this.worldOf(X, Z, this.rows, BIN_DROP)
          out.push({ X, Z, ring, rim, hole, center })
        }
      }
      return out
    },
    // ── Pyramid skin pegs ──────────────────────────────────────────
    // Rendering every lattice point of every layer would be ~1800
    // circles for 16 rows. We render only the "skin" — the perimeter
    // of each layer's grid — which reads as a clean 3D pyramid lattice
    // at a fraction of the element count. Sorted far→near so SVG paints
    // back-to-front.
    pegs3d() {
      return this.pegsWorld
        .map(({ wx, wy, wz }) => {
          const p = this.project(wx, wy, wz)
          return { x: p.x, y: p.y, depth: p.depth }
        })
        .sort((a, b) => a.depth - b.depth)
    },
    // ── Bin grid ───────────────────────────────────────────────────
    // (rows+1)×(rows+1) cells. Each cell is a TUBE: a colored rim at the
    // tray surface plus a darker, smaller, recessed inner polygon
    // (projected at BIN_DROP+TUBE_DROP) — the offset between them creates
    // the well-down-into-the-floor look. On land, the WHOLE winning ring
    // lights up, since payout is radial.
    bins3d() {
      const a = this.activeRound
      const hitRing = a ? Number(a.ring) : -1
      const hitLive = !!a && Number(a.roundStatus) !== 0 && this.ballLanded
      const toPoly = (worldCorners) =>
        worldCorners
          .map((w) => {
            const p = this.project(w[0], w[1], w[2])
            return `${p.x.toFixed(2)},${p.y.toFixed(2)}`
          })
          .join(' ')
      return this.binsWorld
        .map(({ X, Z, ring, rim, hole, center }) => {
          const c = this.project(center[0], center[1], center[2])
          return {
            X, Z, ring,
            rimPoints: toPoly(rim),
            holePoints: toPoly(hole),
            cx: c.x, cy: c.y, depth: c.depth,
            mult: this.multipliers[ring],
            hit: hitLive && ring === hitRing,
          }
        })
        .sort((a, b) => a.depth - b.depth)
    },
    // Rotation-INVARIANT viewBox. As yaw spins, every world point
    // traces a circle of radius hypot(wx,wz) about the central axis
    // (the vertical line through the apex AND the centre bin — both sit
    // at world x=z=0). If we re-fit the box to the current yaw the
    // board appears to zoom/drift; instead we size the box to hold the
    // geometry at ANY yaw, so rotation is pure spin-in-place.
    //   x  ∈ [-R, +R]                    R = max hypot(wx,wz)
    //   y2 = wy·cosP − z1·sinP,  z1 ∈ [-r, r]  → y2 ∈ [wy·cosP ± r·sinP]
    // Depends only on `rows` + the fixed pitch — never on yaw.
    viewBox() {
      const cpit = Math.cos(this.pitch), spit = Math.sin(this.pitch)
      let maxR = 0, yMin = Infinity, yMax = -Infinity
      const consider = (wx, wy, wz) => {
        const r = Math.hypot(wx, wz)
        if (r > maxR) maxR = r
        const yc = wy * cpit
        if (yc - r * spit < yMin) yMin = yc - r * spit
        if (yc + r * spit > yMax) yMax = yc + r * spit
      }
      // The envelope is pinned by the apex (top, narrowest) and the bin
      // tray's outer-corner cells (bottom, widest) — every peg layer
      // sits inside that.
      consider(0, 0, 0)
      for (const X of [-0.5, this.rows + 0.5]) {
        for (const Z of [-0.5, this.rows + 0.5]) {
          const w = this.worldOf(X, Z, this.rows, BIN_DROP)
          consider(w[0], w[1], w[2])
        }
      }
      const pad = 8
      const xExt = maxR + pad
      const h = (yMax - yMin) + pad * 2
      return `${(-xExt).toFixed(1)} ${(yMin - pad).toFixed(1)} ${(2 * xExt).toFixed(1)} ${h.toFixed(1)}`
    },
    // Which ring's chip should flash in the legend below. -1 when there
    // is no active resolved round or the ball hasn't landed yet.
    hitRing() {
      const a = this.activeRound
      if (!a || Number(a.roundStatus) === 0 || !this.ballLanded) return -1
      return Number(a.ring)
    },
    // ── Ball position — projected, per frame ───────────────────────
    ballPosition() {
      this.ballTick // eslint-disable-line no-unused-expressions
      // Resting: parked at the pyramid apex.
      if (!this.ball) {
        const p = this.project(...this.worldOf(0, 0, 0))
        return { sx: p.x, sy: p.y, depth: p.depth, scale: 1, spin: 0, resting: true, done: true }
      }
      const { xBits, zBits, rows, t0, totalDurationS } = this.ball
      const elapsed = (performance.now() - t0) / 1000
      // animateBall() seeds the total duration onto the ball so the
      // read side here stays in lockstep with whatever slowdown / tuning
      // the writer chooses.
      const totalDuration = totalDurationS
      const tNorm = Math.min(1, elapsed / totalDuration)
      const SETTLE = 0.6     // fall from last peg into the rim opening
      const SINK   = 0.5     // sink down inside the tube
      const u = tNorm * (rows + SETTLE + SINK)

      let world
      if (u <= rows) {
        // Walking the pyramid. Which layer-segment are we in?
        const layer = Math.min(rows - 1, Math.floor(u))
        const intra = u - layer
        const ease = easeOutCubic(intra)
        // accumulated grid position at the top of this segment
        let xc = 0, zc = 0
        for (let i = 0; i < layer; i++) { xc += xBits[i]; zc += zBits[i] }
        const nx = xBits[layer], nz = zBits[layer]
        const w0 = this.worldOf(xc, zc, layer)
        const w1 = this.worldOf(xc + nx, zc + nz, layer + 1)
        // X/Z ease smoothly toward the next column; Y is the literal
        // fall plus a small mid-segment hop so it reads as a bounce.
        const wx = w0[0] + (w1[0] - w0[0]) * ease
        const wz = w0[2] + (w1[2] - w0[2]) * ease
        const hop = Math.sin(intra * Math.PI) * LH * 0.16
        const wy = w0[1] + (w1[1] - w0[1]) * intra - hop
        world = [wx, wy, wz]
      } else if (u <= rows + SETTLE) {
        // Falling from the last peg layer down to the tube rim.
        const s = (u - rows) / SETTLE
        const top = this.worldOf(this.ball.finalX, this.ball.finalZ, rows)
        const wy = top[1] + BIN_DROP * (1 - Math.pow(1 - s, 2))
        world = [top[0], wy, top[2]]
      } else {
        // Landed — ball sits at the rim of its bin at full scale. The
        // previous code shrank the ball to ~25% to read as "sinking
        // into the tube", but the visual disappearing-act read worse
        // than just parking the ball on its target square.
        const top = this.worldOf(this.ball.finalX, this.ball.finalZ, rows)
        world = [top[0], top[1] + BIN_DROP, top[2]]
      }
      const p = this.project(...world)
      // Constant size throughout — orthographic projection means depth
      // shouldn't change it, and we don't shrink-on-sink anymore so the
      // ball stays at full size when parked in its winning bin.
      const scale = 1
      const spin = (elapsed * 540) % 360       // 1.5 rev/s — "for fun"
      return {
        sx: p.x, sy: p.y, depth: p.depth, scale, spin,
        resting: false, done: tNorm >= 1,
      }
    },
    // Pegs split at the ball's depth so the ball is correctly occluded:
    // pegs further from the camera render BEFORE the ball, nearer pegs
    // render AFTER it. pegs3d is already depth-sorted ascending, so this
    // is a cheap filter. Recomputes per frame via ballTick (in
    // ballPosition) — fine, it's just comparisons over ~550 entries.
    pegsBehindBall() {
      const bd = this.ballPosition.depth
      return this.pegs3d.filter((p) => p.depth <= bd)
    },
    pegsInFrontBall() {
      const bd = this.ballPosition.depth
      return this.pegs3d.filter((p) => p.depth > bd)
    },
  },
  created() {
    this.captureMyAddress()
    this.refresh()
    this.startPolling()
  },
  mounted() {
    // Kick off the constant turntable spin once the component is live.
    this.startSpin()
  },
  beforeUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval)
    if (this.pollCountdownInterval) clearInterval(this.pollCountdownInterval)
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame)
    if (this.spinFrame) cancelAnimationFrame(this.spinFrame)
  },
  methods: {
    // World coords for lattice point (c,e) at pyramid depth `d`. The
    // layer is centred on the Y axis so the pyramid is symmetric; the
    // optional `yExtra` pushes a point further down (used for the bin
    // tray sitting below the last peg layer).
    worldOf(c, e, d, yExtra = 0) {
      return [
        (c - d / 2) * SP,        // wx
        d * LH + yExtra,         // wy (down is +)
        (e - d / 2) * SP,        // wz
      ]
    },
    // Software 3D projection: yaw about the vertical axis, then pitch
    // about the horizontal. Orthographic — `depth` is the rotated Z,
    // used for back-to-front sort, ball occlusion, and a faint
    // perspective scale. pitch/yaw are reactive (drag to rotate).
    project(wx, wy, wz) {
      const cyaw = Math.cos(this.yaw), syaw = Math.sin(this.yaw)
      const cpit = Math.cos(this.pitch), spit = Math.sin(this.pitch)
      const x1 = wx * cyaw + wz * syaw
      const z1 = -wx * syaw + wz * cyaw
      const y2 = wy * cpit - z1 * spit
      const z2 = wy * spit + z1 * cpit
      return { x: x1, y: y2, depth: z2 }
    },
    // Drive the constant turntable rotation. yaw advances at
    // SPIN_RAD_PER_S; the loop runs from mount to beforeUnmount.
    // User interaction (drag, reset view) has been removed — the
    // board is non-interactive on purpose so the rotation is the
    // single source of camera motion.
    startSpin() {
      if (this.spinFrame) cancelAnimationFrame(this.spinFrame)
      this.spinAnchorYaw = this.yaw
      this.spinT0 = performance.now()
      // Throttle yaw writes — cadence comes from the yawUpdateMs
      // computed so bigger boards (12 / 16 rows) throttle harder than
      // the sparse 8-row board. Reactivity churn dominates per-frame
      // cost on the dense boards; this caps the work the renderer
      // has to do per second.
      let lastUpdate = 0
      const tick = () => {
        const now = performance.now()
        if (now - lastUpdate >= this.yawUpdateMs) {
          const elapsed = (now - this.spinT0) / 1000
          this.yaw = this.spinAnchorYaw + elapsed * SPIN_RAD_PER_S
          lastUpdate = now
        }
        this.spinFrame = requestAnimationFrame(tick)
      }
      this.spinFrame = requestAnimationFrame(tick)
    },
    // Full-rainbow colour per ring: red at the centre ring, sweeping
    // through orange/yellow/green/blue to magenta at the corners. Every
    // ring gets its own clearly-distinct hue. `lightness` lets callers
    // pull a darker shade of the same hue (used for the hole fill so
    // each well matches its rim's colour).
    ringColor(ring, alpha = 0.85, lightness = 55) {
      const span = Math.max(1, this.half)
      const t = Math.min(1, ring / span)          // 0 = centre … 1 = corner
      const hue = t * 300                         // 0° red → 300° magenta
      return `hsla(${hue.toFixed(0)}, 80%, ${lightness}%, ${alpha})`
    },
    async captureMyAddress() {
      try {
        const acct = await this.wallet?.client?.getActiveAccount?.()
        this.myAddress = acct?.address || ''
      } catch (_e) { /* noop */ }
    },
    startPolling() {
      this.pollInterval = setInterval(() => this.refresh(), 5000)
      this.pollCountdownInterval = setInterval(() => {
        const elapsed = (Date.now() - (this.pollLastAt || Date.now())) / 1000
        this.pollSecondsUntilNext = Math.max(0, Math.round(5 - elapsed))
      }, 500)
    },
    async refresh() {
      this.pollLastAt = Date.now()
      this.pollSecondsUntilNext = 5
      let storage
      try {
        storage = await getContractStorage(PLINKO_CONTRACT_ADDRESS)
      } catch (e) {
        console.warn('plinko refresh failed:', e?.message)
        return
      }
      if (!storage) return
      this.rounds = storage.rounds || {}
      this.potBalance = Number((Number(storage.pot || 0) * 1e-6).toFixed(3))
      this.reserveBalance = Number((Number(storage.potReserve || 0) * 1e-6).toFixed(3))
      this.minBet = Number((Number(storage.minBet || 100000) * 1e-6).toFixed(3))
      this.maxBet = Number((Number(storage.maxBet || 10000000) * 1e-6).toFixed(3))
      this.fee = Number((Number(storage.fee || 100000) * 1e-6).toFixed(3))
      if (this.bet > this.betSliderMax) this.bet = this.betSliderMax

      if (!this.myAddress) await this.captureMyAddress()
      if (this.myAddress) {
        const ids = Object.keys(this.rounds)
          .filter((id) => this.rounds[id].player === this.myAddress)
          .map((id) => Number(id))
          .sort((a, b) => b - a)
        this.myRoundIds = ids
        if (this.activeRoundId == null && ids.length) {
          this.activeRoundId = ids[0]
          this.lastSeenStatus = Number(this.rounds[ids[0]].roundStatus)
        }
        const active = this.activeRound
        if (active) {
          const status = Number(active.roundStatus)
          if (status !== 0 && this.lastSeenStatus === 0) {
            const rows = Number(active.rows)
            // Snap the board to the round's geometry so the projected
            // pyramid + bin grid match the path being replayed.
            if (this.rows !== rows) this.rows = rows
            const r = Number(active.risk)
            if (Number.isFinite(r) && this.risk !== r) this.risk = r
            this.animateBall(
              rows,
              bitsFromStoragePath(active.xPath, rows),
              bitsFromStoragePath(active.zPath, rows),
            )
            this.blockchainStatus = this.summarizeResolve(active)
          }
          this.lastSeenStatus = status
        }
      }
    },
    // Click handler for the "recent drops" list — selects the round and
    // replays its 3D drop. Pending rounds (status 0) just get selected.
    replayRound(id) {
      this.activeRoundId = id
      const r = this.rounds[id]
      if (!r) return
      const status = Number(r.roundStatus)
      this.lastSeenStatus = status
      if (status === 0) {
        this.blockchainStatus = `Round #${id} still pending oracle resolve.`
        return
      }
      const rows = Number(r.rows)
      if (this.rows !== rows) this.rows = rows
      const rsk = Number(r.risk)
      if (Number.isFinite(rsk) && this.risk !== rsk) this.risk = rsk
      this.animateBall(
        rows,
        bitsFromStoragePath(r.xPath, rows),
        bitsFromStoragePath(r.zPath, rows),
      )
      this.blockchainStatus = `Replaying round #${id} — ${this.summarizeResolve(r)}`
    },
    summarizeResolve(r) {
      const status = Number(r.roundStatus)
      const payout = Number(r.payout || 0) * 1e-6
      const bet = Number(r.bet || 0) * 1e-6
      const at = `(${r.finalX},${r.finalZ}) ring ${r.ring}`
      if (status === 1) {
        const ratio = (bet > 0 ? payout / bet : 0).toFixed(2)
        return `Win! Landed ${at} for ${payout.toFixed(3)} tez (${ratio}×).`
      }
      if (status === 2) return `Push — got your bet back (${payout.toFixed(3)} tez).`
      if (status === 3) return `Loss. ${at} paid ${payout.toFixed(3)} tez.`
      return 'Resolved.'
    },
    setBetPercent(pct) {
      const max = this.betSliderMax
      const val = Number(((max - this.minBet) * (pct / 100) + this.minBet).toFixed(3))
      this.bet = Math.max(this.minBet, Math.min(max, val))
    },
    // User picked a new board size. Drop any parked / in-flight ball so it
    // isn't left rendered against the old row geometry.
    setRows(r) {
      if (this.rows === r) return
      this.rows = r
      this.resetBall()
    },
    // Stop the rAF loop and clear all ball state back to "no ball".
    // Yaw isn't touched here — the constant turntable spin runs
    // independently and keeps going across row-switches.
    resetBall() {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = 0
      this.ball = null
      this.ballLanded = false
      this.ballSettleAt = 0
    },
    animateBall(rows, xBits, zBits) {
      const finalX = xBits.reduce((a, b) => a + b, 0)
      const finalZ = zBits.reduce((a, b) => a + b, 0)
      this.ballLanded = false
      const t0 = performance.now()
      // 20% slowdown vs the previous timing — duration ×= 1.25. Per-row
      // total now lands at:
      //   8 rows  → 4.25s
      //   12 rows → 5.75s
      //   16 rows → 7.25s
      // The continuous turntable spin (driven by startSpin()) runs
      // independently across all of this; the drop animation only
      // touches ball position, not yaw.
      const totalDurationS = (0.30 * rows + 0.6 + 0.4) * 1.25
      this.ball = { rows, xBits, zBits, finalX, finalZ, t0, totalDurationS }
      this.ballSettleAt = t0 + totalDurationS * 1000
      const tick = () => {
        if (!this.ball) return
        this.ballTick++
        const pos = this.ballPosition
        if (pos?.done) {
          if (performance.now() > this.ballSettleAt + 500) {
            // Touchdown: stop the rAF loop but keep `this.ball` set so
            // the ball stays parked in its bin. Flipping ballLanded
            // lights the winning cell.
            this.ballLanded = true
            this.animationFrame = 0
            return
          }
        }
        this.animationFrame = requestAnimationFrame(tick)
      }
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = requestAnimationFrame(tick)
    },
    useWalletProvider() { this.tezos.setWalletProvider(this.wallet) },
    async drop() {
      if (this.previewMode) {
        this.blockchainStatus = 'TXL holder preview — drops disabled. Coming soon.'
        return
      }
      console.debug('[plinko] drop clicked', {
        canDrop: this.canDrop,
        wallet: !!this.wallet,
        bet: this.bet, fee: this.fee, rows: this.rows, risk: this.risk,
      })
      if (!this.canDrop) {
        this.blockchainStatus = this.cantDropReason || 'Drop unavailable right now.'
        return
      }
      this.blockchainStatus = 'Reading wallet…'
      let acct
      try {
        acct = await this.wallet.client.getActiveAccount()
      } catch (e) {
        console.error('[plinko] getActiveAccount failed:', e)
        this.blockchainStatus = 'Wallet read failed — try Reset wallet.'
        return
      }
      if (!acct) {
        this.blockchainStatus = 'Connect your wallet first (click the SYNC WALLET button).'
        return
      }
      // v3: play() now takes (rows, risk, userNonce, commitId) and the
      // amount must cover bet + fee + oracleFee. The bits driving the
      // ball's path are derived in onRandomFulfilled from the player's
      // nonce + the oracle's commit-revealed preimage — the operator
      // can't pre-pick a preimage that favors a specific landing bin.
      const userNonceHex = generateUserNonceHex()
      this.lastUserNonceHex = userNonceHex
      let commitId
      try {
        commitId = await pickEligibleCommitId()
      } catch (e) {
        this.blockchainStatus = `Cannot drop: ${e.message}`
        console.warn('[plinko] pickEligibleCommitId failed', e)
        return
      }
      this.lastCommitId = commitId
      const totalTez = Number(this.bet) + Number(this.fee) + Number(this.oracleFee)
      const totalStr = totalTez.toFixed(6)
      this.dropping = true
      this.blockchainStatus = (
        `Submitting drop (${totalStr} tez; commit ${commitId}; ` +
        `your entropy: ${userNonceHex.slice(2, 10)}…)…`
      )
      try {
        this.useWalletProvider()
        const contract = await this.tezos.wallet.at(PLINKO_CONTRACT_ADDRESS)
        const op = await contract.methodsObject.play({
          rows: this.rows,
          risk: this.risk,
          userNonce: userNonceHex,
          commitId,
        }).send({ amount: totalStr })
        this.blockchainStatus = `Broadcast — waiting for confirmation (${op.opHash.slice(0, 12)}…)`
        await op.confirmation()
        this.blockchainStatus = 'Drop confirmed — waiting for oracle to settle.'
        await this.refresh()
        if (this.myRoundIds.length) {
          this.activeRoundId = this.myRoundIds[0]
          this.lastSeenStatus = 0
        }
      } catch (err) {
        const msg = err?.message || String(err)
        console.error('[plinko] drop failed:', err)
        if (/aborted|cancel|denied/i.test(msg)) {
          this.blockchainStatus = 'Drop cancelled in wallet.'
        } else if (/bet too big/i.test(msg)) {
          this.blockchainStatus = 'Bet too big — try lower.'
        } else if (/bet too small/i.test(msg)) {
          this.blockchainStatus = `Bet too small — min ${this.minBet} tez.`
        } else if (/setWalletProvider|not a function/i.test(msg)) {
          this.blockchainStatus = 'Wallet provider missing — refresh the page.'
        } else {
          this.blockchainStatus = `Drop failed: ${msg.slice(0, 140)}`
        }
      } finally {
        this.dropping = false
      }
    },
  },
}
</script>

<template>
  <div class="plinkoRoot">
    <div class="plinkoHeader">
      <div class="plinkoTitle">PLINKO 3D</div>
      <div class="plinkoSub">{{ riskLabel }} risk · {{ rows }} rows · pot {{ potBalance }} tez</div>
    </div>

    <div class="controlsGrid">
      <div class="controlRow">
        <label class="ctrlLabel">Rows</label>
        <div class="segmented">
          <button
            v-for="r in rowOptions"
            :key="r"
            :class="['segBtn', rows === r ? 'segBtn--on' : '']"
            @click="setRows(r)"
          >{{ r }}</button>
        </div>
      </div>

      <div class="controlRow">
        <label class="ctrlLabel">Risk</label>
        <div class="segmented">
          <button :class="['segBtn', risk === 0 ? 'segBtn--on' : '']" @click="risk = 0">Low</button>
          <button :class="['segBtn', risk === 1 ? 'segBtn--on' : '']" @click="risk = 1">Med</button>
          <button :class="['segBtn risk--hi', risk === 2 ? 'segBtn--on' : '']" @click="risk = 2">High</button>
        </div>
      </div>

      <div class="controlRow">
        <label class="ctrlLabel">Bet</label>
        <div class="betSliderWrap">
          <input
            type="range"
            class="betSlider"
            :min="minBet"
            :max="betSliderMax"
            step="0.05"
            v-model.number="bet"
          />
          <div class="betValue">{{ bet.toFixed(3) }} tez</div>
        </div>
      </div>
      <div class="controlRow">
        <label class="ctrlLabel"></label>
        <div class="quickPicks">
          <button class="quickBtn" @click="setBetPercent(25)">25%</button>
          <button class="quickBtn" @click="setBetPercent(50)">50%</button>
          <button class="quickBtn" @click="setBetPercent(75)">75%</button>
          <button class="quickBtn" @click="setBetPercent(100)">Max</button>
        </div>
      </div>

      <div class="controlRow controlRow--cta">
        <button
          :class="['dropBtn', canDrop ? '' : 'dropBtn--off', dropping ? 'dropBtn--busy' : '']"
          :disabled="!canDrop"
          @click="drop"
        >
          <span v-if="dropping">DROPPING…</span>
          <span v-else>DROP BALL · {{ (bet + fee).toFixed(3) }} tez</span>
        </button>
      </div>
    </div>

    <div class="boardFrame">
      <svg
        :viewBox="viewBox"
        class="board"
      >
        <defs>
          <!-- Pegs: a soft neutral gray, lightly domed (not gold). -->
          <radialGradient id="pegGlow" cx="42%" cy="38%" r="62%">
            <stop offset="0%" stop-color="#c2c6cf" />
            <stop offset="100%" stop-color="#6b7180" />
          </radialGradient>
          <!-- Ball: matte red. Gentle top-light, no bright white
               hotspot — keeps it from looking glossy/plastic. -->
          <radialGradient id="ballGrad" cx="40%" cy="36%" r="68%">
            <stop offset="0%" stop-color="#f0726f" />
            <stop offset="55%" stop-color="#d63b38" />
            <stop offset="100%" stop-color="#8f201d" />
          </radialGradient>
        </defs>

        <!-- Bin grid (drawn first — it's the floor; the pyramid + ball
             sit above it). Each cell is a TUBE: a colored rim at the
             tray surface, plus a darker, smaller, recessed hole drawn
             on top — together they read as a square well the ball drops
             into. On a winning ring, both rim and hole light up.
             Rendered as two parallel v-fors (all rims, then all holes)
             so every hole paints over its own rim while the cells
             themselves still tile without overlap. -->
        <polygon
          v-for="b in bins3d"
          :key="`rim-${b.X}-${b.Z}`"
          :points="b.rimPoints"
          :fill="ringColor(b.ring)"
          :class="['bin', 'binRim', b.hit ? 'bin--hit' : '']"
        />
        <polygon
          v-for="b in bins3d"
          :key="`hole-${b.X}-${b.Z}`"
          :points="b.holePoints"
          :fill="ringColor(b.ring, 0.95, 22)"
          :class="['bin', 'binHole', b.hit ? 'bin--hit' : '']"
        />

        <!-- Pegs are split at the ball's depth so the ball is correctly
             occluded — pegs further from the camera draw BEHIND it,
             nearer pegs draw IN FRONT. Both lists stay depth-sorted. -->
        <circle
          v-for="(p, i) in pegsBehindBall"
          :key="`pegB-${i}`"
          :cx="p.x"
          :cy="p.y"
          :r="pegRadius"
          fill="url(#pegGlow)"
          class="peg"
        />

        <!-- The ball. The <g> carries position + a spin transform; the
             two dark spots make the rotation visible. The glint is a
             separate, NON-rotating highlight so the light stays put. -->
        <g
          :transform="`translate(${ballPosition.sx} ${ballPosition.sy}) rotate(${ballPosition.spin}) scale(${ballPosition.scale})`"
          :class="['ballG', ballPosition.resting ? 'ballG--resting' : '']"
        >
          <circle :r="ballRadius" fill="url(#ballGrad)" class="ball" />
          <circle
            :cx="ballRadius * 0.34" :cy="ballRadius * 0.30"
            :r="ballRadius * 0.26" fill="#7f1d1d" opacity="0.85"
          />
          <circle
            :cx="-ballRadius * 0.40" :cy="ballRadius * 0.10"
            :r="ballRadius * 0.20" fill="#a52121" opacity="0.8"
          />
        </g>
        <!-- Faint top-light glint — kept subtle so the ball reads matte
             rather than glossy. -->
        <circle
          :cx="ballPosition.sx - ballRadius * 0.30 * ballPosition.scale"
          :cy="ballPosition.sy - ballRadius * 0.34 * ballPosition.scale"
          :r="ballRadius * 0.17 * ballPosition.scale"
          fill="#fff" opacity="0.2"
        />

        <!-- Pegs nearer the camera than the ball — drawn last so they
             overlap (occlude) it. -->
        <circle
          v-for="(p, i) in pegsInFrontBall"
          :key="`pegF-${i}`"
          :cx="p.x"
          :cy="p.y"
          :r="pegRadius"
          fill="url(#pegGlow)"
          class="peg"
        />
      </svg>
    </div>

    <!-- Ring → multiplier legend. The board cells are unlabeled; this
         strip is the source of truth for payouts. The winning ring's
         chip flashes gold the moment the ball lands. -->
    <div class="ringLegend">
      <div
        v-for="(m, ring) in multipliers"
        :key="`ring-${ring}`"
        :class="['ringChip', ring === hitRing ? 'ringChip--hit' : '']"
        :style="{ background: ringColor(ring, 0.32), borderColor: ringColor(ring, 0.75) }"
      >
        <span class="ringIdx">ring {{ ring }}</span>
        <span class="ringMult">{{ Number(m).toString().replace(/\.0$/, '') }}×</span>
      </div>
    </div>

    <div class="statusPanel">
      <div class="statusLine">{{ blockchainStatus }}</div>
      <div class="statusMeta">
        <span class="metaPill">{{ pollHint }}</span>
        <span class="metaPill">pot {{ potBalance }} tez</span>
        <span class="metaPill">reserve {{ reserveBalance }} tez</span>
        <!-- v3 — entropy receipt. Hover for the trust-model summary. -->
        <span
          v-if="lastUserNonceHex"
          class="metaPill"
          :title="'Your 32 random bytes mix into the oracle’s seed alongside its commit-revealed preimage — neither the operator nor you alone can bias the result.'"
        >entropy {{ lastUserNonceHex.slice(2, 10) }}… · commit {{ lastCommitId }}</span>
      </div>
    </div>

    <div v-if="myRoundIds.length" class="recentRounds">
      <div class="recentHdr">RECENT DROPS</div>
      <div class="recentList">
        <div
          v-for="id in myRoundIds.slice(0, 8)"
          :key="id"
          :class="['recentItem', `recent--${rounds[id]?.roundStatus}`]"
          @click="replayRound(id)"
          :title="rounds[id]?.roundStatus != 0 ? 'Click to replay this drop' : ''"
        >
          <span class="recentId">#{{ id }}</span>
          <span class="recentSpec">{{ rounds[id]?.rows }}r · {{ RISK_LABELS[Number(rounds[id]?.risk)] || '?' }}</span>
          <span class="recentSlot">
            <template v-if="rounds[id]?.roundStatus != 0">
              ({{ rounds[id]?.finalX }},{{ rounds[id]?.finalZ }}) r{{ rounds[id]?.ring }}
            </template>
            <template v-else>pending</template>
          </span>
          <span class="recentPay">{{ ((Number(rounds[id]?.payout || 0)) * 1e-6).toFixed(3) }} tez</span>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="howToBtn"
      :aria-expanded="showInfo"
      @click="showInfo = !showInfo"
    >
      How to play <span aria-hidden="true">{{ showInfo ? '▲' : '▼' }}</span>
    </button>
    <div v-if="showInfo" class="infoBlock">
      <div v-for="(line, i) in info" :key="i" class="infoLine">· {{ line }}</div>
    </div>
  </div>
</template>

<style scoped>
.plinkoRoot {
  display: flex; flex-direction: column; gap: 14px;
  padding: 16px;
  max-width: 680px; width: 100%; margin: 0 auto;
  color: var(--ad-text-1);
  /* box-sizing: border-box so the 16px padding lives INSIDE the
     max-width: 100% bound instead of pushing total width past the
     parent's content box. Combined with min-width:0 + overflow-x:hidden
     this keeps every inner row inside the viewport on narrow phones. */
  box-sizing: border-box;
  min-width: 0;
  overflow-x: hidden;
}
.plinkoRoot,
.plinkoRoot * { box-sizing: border-box; min-width: 0; max-width: 100%; }

.plinkoHeader { text-align: center; }
.plinkoTitle {
  font-family: var(--ad-font-display);
  font-size: 28px;
  letter-spacing: 0.12em;
  background: var(--ad-grad-fire);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.plinkoSub {
  font-family: var(--ad-font-mono);
  font-size: 11px;
  color: var(--ad-text-3);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-top: 2px;
}

.controlsGrid {
  display: flex; flex-direction: column; gap: 8px;
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-md);
  padding: 12px;
}
.controlRow { display: flex; align-items: center; gap: 12px; }
.ctrlLabel {
  min-width: 50px;
  font-family: var(--ad-font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ad-text-3);
}
.segmented {
  display: flex; gap: 4px; flex: 1;
  background: var(--ad-bg-elev-2);
  border-radius: var(--ad-r-pill);
  padding: 3px;
}
.segBtn {
  flex: 1; border: 0;
  background: transparent;
  color: var(--ad-text-2);
  padding: 6px 10px;
  font-family: var(--ad-font-mono);
  font-size: 12px;
  border-radius: var(--ad-r-pill);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.segBtn:hover { color: var(--ad-text-1); transform: translateY(-1px); }
.segBtn--on {
  background: var(--ad-grad-violet);
  color: #fff;
  box-shadow: var(--ad-glow-violet);
}
.segBtn.risk--hi.segBtn--on { background: var(--ad-grad-fire); box-shadow: var(--ad-glow-gold); }

.betSliderWrap { display: flex; align-items: center; flex: 1; gap: 12px; }
.betSlider {
  flex: 1; appearance: none; -webkit-appearance: none;
  height: 4px;
  background: linear-gradient(90deg, var(--ad-violet-3), var(--ad-gold-2));
  border-radius: 999px;
  outline: none;
}
.betSlider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px; height: 18px;
  background: var(--ad-grad-gold);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: var(--ad-glow-gold);
}
.betSlider::-moz-range-thumb {
  width: 18px; height: 18px;
  background: var(--ad-grad-gold);
  border-radius: 50%;
  cursor: pointer;
  border: 0;
}
.betValue {
  font-family: var(--ad-font-mono);
  font-size: 13px;
  color: var(--ad-gold-1);
  min-width: 76px; text-align: right;
}
.quickPicks { display: flex; gap: 6px; flex: 1; }
.quickBtn {
  flex: 1;
  background: var(--ad-bg-elev-2);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-sm);
  color: var(--ad-text-2);
  font-family: var(--ad-font-mono);
  font-size: 11px;
  padding: 5px 0;
  cursor: pointer;
  transition: all 0.15s ease;
}
.quickBtn:hover {
  background: var(--ad-bg-elev-3);
  color: var(--ad-gold-1);
  border-color: var(--ad-gold-3);
}

.controlRow--cta { justify-content: center; }
.dropBtn {
  width: 100%;
  background: var(--ad-grad-fire);
  border: 0;
  border-radius: var(--ad-r-md);
  color: #ffffff;
  font-family: var(--ad-font-display);
  font-size: 18px;
  letter-spacing: 0.08em;
  padding: 12px 18px;
  cursor: pointer;
  box-shadow: var(--ad-glow-gold);
  transition: transform 0.12s ease, box-shadow 0.2s ease;
}
.dropBtn:hover:not(:disabled) { transform: translateY(-1px); }
.dropBtn:active:not(:disabled) { transform: translateY(0); }
.dropBtn--off {
  background: var(--ad-bg-elev-2);
  color: var(--ad-text-3);
  box-shadow: none;
  cursor: not-allowed;
}
.dropBtn--busy { animation: dropPulse 1.2s ease-in-out infinite; }
@keyframes dropPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(200, 16, 46, 0.6); }
  50%      { box-shadow: 0 0 0 8px rgba(200, 16, 46, 0); }
}

.boardFrame {
  background:
    radial-gradient(ellipse at 50% 0%, rgba(21, 41, 74, 0.14) 0%, transparent 62%),
    var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-lg);
  padding: 10px;
}
.board {
  width: 100%; display: block;
  cursor: default;
  /* Block pointer-events so a stray click can't capture or interrupt
     the constant turntable rotation. */
  pointer-events: none;
}
/* Neutral soft shadow — pegs are gray now, not glowing gold. */
.peg { filter: drop-shadow(0 0.5px 1px rgba(0, 0, 0, 0.45)); }

/* Bin grid cells — rim fill is the ring color (set inline); the inner
   hole is a deep, near-black void to read as the inside of a well. */
.bin {
  transition: filter 0.2s ease;
}
.binRim {
  stroke: rgba(0, 0, 0, 0.7);
  stroke-width: 0.5;
  filter: drop-shadow(0 0.5px 0.5px rgba(0, 0, 0, 0.45));
}
.binHole {
  /* fill set inline — darker shade of the rim's ring colour. */
  stroke: rgba(0, 0, 0, 0.85);
  stroke-width: 0.4;
}
/* forwards: the winning ring stays lit until the next drop. */
.bin--hit { animation: binHit 0.9s ease-out forwards; }
@keyframes binHit {
  0%   { filter: brightness(1.0) drop-shadow(0 0 0 transparent); }
  25%  { filter: brightness(2.9) drop-shadow(0 0 10px var(--ad-gold-1)); }
  100% { filter: brightness(1.8) drop-shadow(0 0 5px var(--ad-gold-1)); }
}

/* Plain dark drop-shadow — no red glow — so the ball reads matte. */
.ball { filter: drop-shadow(0 1.5px 3px rgba(0, 0, 0, 0.45)); }
.ballG--resting {
  animation: ballRest 2.4s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: center;
}
@keyframes ballRest {
  0%, 100% { filter: drop-shadow(0 1.5px 2px rgba(0, 0, 0, 0.4)); }
  50%      { filter: drop-shadow(0 1.5px 5px rgba(0, 0, 0, 0.55)); }
}

/* Ring → multiplier legend strip. The board no longer carries payout
   labels, so this is the primary place to read what each ring pays. */
.ringLegend {
  display: flex; flex-wrap: wrap; gap: 7px; justify-content: center;
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-md);
  padding: 10px 12px;
}
.ringChip {
  display: flex; flex-direction: column; align-items: center;
  min-width: 64px;
  padding: 8px 12px;
  border-radius: var(--ad-r-md);
  border: 1.5px solid transparent;   /* colour set inline via ringColor */
  font-family: var(--ad-font-mono);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.ringIdx {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ad-text-2);
}
.ringMult {
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
  margin-top: 2px;
}
/* Win flash — held forwards so the chip stays highlighted until the
   next drop clears the ballLanded state. */
.ringChip--hit {
  animation: chipHit 1.4s ease-out forwards;
  z-index: 1;
}
@keyframes chipHit {
  0% {
    box-shadow: 0 0 0 0 rgba(200, 16, 46, 0.0);
    transform: scale(1);
  }
  18% {
    box-shadow: 0 0 22px 6px rgba(200, 16, 46, 0.95);
    transform: scale(1.22);
  }
  100% {
    box-shadow: 0 0 9px 2px rgba(200, 16, 46, 0.6);
    transform: scale(1.06);
  }
}

.statusPanel {
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-md);
  padding: 10px 12px;
}
.statusLine {
  font-family: var(--ad-font-body);
  font-size: 13px;
  color: var(--ad-text-1);
}
.statusMeta { display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
.metaPill {
  font-family: var(--ad-font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  background: var(--ad-bg-elev-2);
  border-radius: var(--ad-r-pill);
  color: var(--ad-text-3);
}

.recentRounds {
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-md);
  padding: 8px 12px;
}
.recentHdr {
  font-family: var(--ad-font-mono);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--ad-text-3);
  text-transform: uppercase;
  margin-bottom: 6px;
}
.recentList { display: flex; flex-direction: column; gap: 4px; }
.recentItem {
  display: flex; gap: 8px; align-items: center;
  padding: 5px 8px;
  background: var(--ad-bg-elev-2);
  border-radius: var(--ad-r-sm);
  font-family: var(--ad-font-mono);
  font-size: 11px;
  color: var(--ad-text-2);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}
.recentItem:hover { background: var(--ad-bg-elev-3); transform: translateX(2px); }
.recentId   { min-width: 32px; color: var(--ad-text-3); }
.recentSpec { min-width: 60px; }
.recentSlot { min-width: 86px; }
.recentPay  { margin-left: auto; }
.recent--1 { color: var(--ad-green-1); }
.recent--3 { color: var(--ad-red-1); }
.recent--2 { color: var(--ad-text-2); }
.recent--0 { color: var(--ad-violet-1); font-style: italic; }

.howToBtn {
  align-self: center;
  background: var(--ad-bg-elev-2);
  border: 1px solid var(--ad-border-soft);
  border-radius: var(--ad-r-sm);
  color: var(--ad-text-2);
  font-family: var(--ad-font-mono);
  font-size: 11px;
  letter-spacing: 0.04em;
  padding: 5px 14px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.howToBtn:hover {
  background: var(--ad-bg-elev-3);
  color: var(--ad-gold-1);
  border-color: var(--ad-gold-3);
}
.howToBtn span { font-size: 9px; margin-left: 4px; }
.infoBlock {
  font-family: var(--ad-font-mono);
  font-size: 11px;
  color: var(--ad-text-3);
  line-height: 1.55;
}
.infoLine { padding: 1px 0; }

/* ─── Mobile ────────────────────────────────────────────────────────
   Plinko's 3D canvas already self-caps at max-width: 100%. The chrome
   around it has historically overrun the right edge on phones because:
     - .controlsGrid had outer padding 16px on a width:100% parent
       (now fixed via box-sizing on .plinkoRoot/*)
     - .controlRow has gap: 12px between a fixed-width label and two
       flex children, and the inner .segmented row carries 4px gaps
       between 8+ row-count buttons — on a 320–360px viewport the
       label + gap eats 60+ px before the segmented row even starts
     - .recentList chip rows have min-width fields that, combined,
       want >300px per row
   The rules below shrink rows + label, drop horizontal padding, and
   stack the bet-row label above the slider so the slider can use the
   full row width. */
@media (max-width: 480px) {
  .plinkoRoot { padding: 8px; gap: 10px; }
  .controlsGrid { padding: 10px; gap: 6px; }
  .controlRow { gap: 6px; }
  .ctrlLabel { min-width: 36px; font-size: 10px; letter-spacing: 0.04em; }
  .segmented { padding: 2px; gap: 2px; }
  .segBtn { font-size: 10px; padding: 5px 4px; }
  .betValue { font-size: 11px; min-width: 0; }
  .quickPicks { gap: 4px; }
  .quickBtn { font-size: 10px; padding: 4px 0; }
  .dropBtn { font-size: 14px; padding: 10px 12px; letter-spacing: 0.04em; }
  .recentHdr { font-size: 10px; }
  .recentId { min-width: 28px; }
  .recentSpec { min-width: 0; }
  .recentSlot { min-width: 0; }
  .recentPay { min-width: 0; }
  .infoBlock { font-size: 10px; }
  .howToBtn { font-size: 11px; padding: 6px 10px; }
  .howToBtn span { font-size: 8.5px; }
}
@media (min-width: 481px) and (max-width: 768px) {
  .infoBlock { font-size: 11px; }
}
</style>
